import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Alert,
  CircularProgress, Chip, List, ListItem, ListItemText, Divider,
  Accordion, AccordionSummary, AccordionDetails,
  Stepper, Step, StepLabel, IconButton, Avatar, Paper, Stack,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  CloudUploadOutlined as UploadIcon,
  ExpandMore as ExpandMoreIcon,
  AccountBalanceWalletOutlined as WalletIcon,
  FactCheckOutlined as VerifyIcon,
  CheckCircleOutline as CheckIcon,
  WarningAmberOutlined as WarningIcon,
  HelpOutlineOutlined as NotFoundIcon,
  AddCircleOutline as DepositIcon,
  RemoveCircleOutline as WithdrawIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import * as actions from '../../../../redux/actions';
import { confirmAsync } from '../../../../utils/functions/globalDialog';
import { preprocessDocumentImage } from '../../../../utils/imagePreprocess';
import { formatCurrency } from '../../utils/format';

const STEPS = ['อัปโหลดและอ่านรายการ (OCR)', 'ตรวจสอบกับรายการในระบบ', 'ยืนยันและบันทึก'];

const STATUS_META = {
  matched: { label: 'ตรงกัน', color: 'success', accent: '#22c55e', Icon: CheckIcon },
  mismatched: { label: 'ไม่ตรงกัน', color: 'warning', accent: '#f59e0b', Icon: WarningIcon },
  not_found: { label: 'ไม่พบในรูป', color: 'default', accent: '#94a3b8', Icon: NotFoundIcon },
};

const BALANCE_MATCH_TOLERANCE = 0.01;

// รูปหน้าจอแอปธนาคารเป็นภาพดิจิทัลสี ไม่ใช่ภาพถ่ายเอกสารกระดาษ (แสงสม่ำเสมออยู่แล้ว ไม่มี
// เงา/มุมเอียงแบบภาพถ่าย) เลยใช้ preprocessDocumentImage (คง contrast/สีไว้ แค่ปรับคมชัด+ขนาด)
// แทน preprocessReceiptImage ที่ทำ grayscale + adaptive threshold (ออกแบบมาสำหรับภาพถ่ายบิล
// กระดาษที่แสงไม่สม่ำเสมอ) — การ binarize ภาพ UI ที่คมอยู่แล้วเสี่ยงทำลายรายละเอียดตัวเลขมากกว่าช่วย
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function dataUrlBase64ToFile(base64, filename) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: 'image/jpeg' });
}

const formatThaiDay = (ymd) => (ymd
  ? new Date(`${ymd}T00:00:00`).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' })
  : '');

function CheckRow({ label, value, bold, color }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={1}>
      <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 400 }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 400, whiteSpace: 'nowrap', color }}>
        {value == null ? '-' : `${formatCurrency(value)} บาท`}
      </Typography>
    </Stack>
  );
}

// กล่องตรวจยอดคงเหลือ: ยอดในระบบ ณ สิ้นวันที่ตรวจ เทียบยอดในรูป + ส่วนต่างเทียบจุดตรวจครั้งก่อน
// (diff = ยอดในรูป − ยอดในระบบ, change = diff − ส่วนต่างครั้งก่อน) ok = change เป็น 0
function BalanceCheckBox({
  loading, error, checkDate, systemBalance, bankBalance, diff, previous, previousDiff, change, ok, amountDiffs,
}) {
  const theme = useTheme();
  if (loading) {
    return (
      <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">กำลังตรวจยอดคงเหลือ...</Typography>
      </Box>
    );
  }
  if (error) {
    return <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>ตรวจยอดคงเหลือไม่สำเร็จ: {error}</Alert>;
  }
  if (systemBalance == null) return null;
  if (bankBalance == null) {
    return <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>อ่านยอดคงเหลือจากรูปไม่ได้ — ตรวจยอดคงเหลือไม่ได้</Alert>;
  }
  const accent = ok ? theme.palette.success.main : theme.palette.warning.main;
  const hasOldDiff = previous && Math.abs(previousDiff) >= BALANCE_MATCH_TOLERANCE;
  let chipLabel = 'ยอดตรงกัน';
  if (ok && hasOldDiff) chipLabel = 'วันนี้ยอดถูกต้อง (ส่วนต่างไม่เพิ่มจากครั้งก่อน)';
  if (!ok) {
    chipLabel = `${previous ? 'ส่วนต่างเพิ่มขึ้น' : 'ต่างกัน'} ${formatCurrency(Math.abs(change))} บาท `
      + `(${change > 0 ? 'ธนาคารมีเงินมากกว่าระบบ' : 'ธนาคารมีเงินน้อยกว่าระบบ'})`;
  }
  return (
    <Paper
      variant="outlined"
      sx={{ mb: 2.5, p: 2, borderRadius: 2.5, borderColor: alpha(accent, 0.4), bgcolor: alpha(accent, 0.07) }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        {ok ? <CheckIcon sx={{ color: accent }} /> : <WarningIcon sx={{ color: accent }} />}
        <Typography variant="subtitle2" fontWeight={700}>ตรวจยอดคงเหลือ ณ สิ้นวันที่ {formatThaiDay(checkDate)}</Typography>
      </Stack>
      <Stack spacing={0.25}>
        <CheckRow label="ยอดในระบบ (ยอดตั้งต้น + ทุกรายการเข้า-ออก)" value={systemBalance} />
        <CheckRow label="ยอดคงเหลือในรูป" value={bankBalance} />
        <Divider sx={{ my: 0.25 }} />
        <CheckRow label="ส่วนต่าง" value={diff} bold />
        {previous && (
          <>
            <CheckRow label={`ส่วนต่างครั้งก่อน (${formatThaiDay(previous.date)})`} value={previousDiff} />
            <CheckRow label="ส่วนต่างเพิ่มขึ้นวันนี้" value={change} bold color={ok ? undefined : accent} />
          </>
        )}
      </Stack>
      <Chip size="small" sx={{ mt: 1, fontWeight: 700, bgcolor: accent, color: '#fff', maxWidth: '100%' }} label={chipLabel} />
      {ok && hasOldDiff && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          ส่วนต่าง {formatCurrency(previousDiff)} บาท ค้างมาตั้งแต่ก่อนหน้า (เช่น ยอดตั้งต้นไม่ตรงธนาคาร) ไม่เกี่ยวกับรายการวันนี้
        </Typography>
      )}
      {!ok && (
        <Box sx={{ mt: 1 }}>
          {amountDiffs.length > 0 && (
            <>
              <Typography variant="caption" fontWeight={700} display="block">รายการที่ยอดในระบบไม่ตรงกับในรูป (เช่น หักภาษี ณ ที่จ่าย):</Typography>
              {amountDiffs.map((d) => (
                <Typography key={d.payment_number} variant="caption" display="block">
                  • {d.payment_number}: ระบบ {formatCurrency(d.systemAmount)} / ในรูป {formatCurrency(d.bankAmount)}
                  {' '}(ต่าง {formatCurrency(Math.abs(d.systemAmount - d.bankAmount))})
                </Typography>
              ))}
            </>
          )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            สาเหตุอื่นที่เป็นไปได้: แนบรูปไม่ครบ, มีรายการที่ยังไม่ได้ลงระบบ, ลงวันที่ในระบบไม่ตรงวันโอนจริง หรือ AI อ่านยอดคงเหลือผิด
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

function SystemPaymentsAccordion({ payments, totalCount }) {
  if (!payments || payments.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2.5, borderRadius: 2, borderStyle: 'dashed' }}>
        <Typography variant="caption" color="text.secondary">
          {totalCount > 0
            ? `มี ${totalCount} payment ในระบบของวันนี้ แต่ไม่มีใบไหนอยู่ในช่วงวันของรูป (±3 วัน) — ตรวจสอบวันในรูปหรือช่วงวันที่ตั้งไว้`
            : 'ไม่มี payment ในระบบที่ยังไม่ยืนยันในช่วงวันนี้'}
        </Typography>
      </Paper>
    );
  }
  return (
    <Accordion sx={{ mb: 2.5, borderRadius: 2, '&:before': { display: 'none' } }} variant="outlined" defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2" fontWeight={600}>
          รายการที่ยังไม่ยืนยันในระบบ ที่อยู่ในช่วงวันที่เดียวกับรูป ({payments.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <List dense disablePadding>
          {payments.map((p) => (
            <ListItem key={p._id} divider disableGutters sx={{ px: 1 }}>
              <ListItemText
                primary={`${p.payment_number} — ${formatCurrency(p.totalAmount)} บาท`}
                secondary={new Date(p.date).toLocaleDateString('th-TH')}
              />
            </ListItem>
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
}

function ResultGroup({ statusKey, items }) {
  if (items.length === 0) return null;
  const meta = STATUS_META[statusKey];
  const { Icon } = meta;
  return (
    <Box sx={{ mb: 2.5 }}>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
        <Icon sx={{ fontSize: 18, color: meta.accent }} />
        <Typography variant="subtitle2" fontWeight={700}>{meta.label} ({items.length})</Typography>
      </Stack>
      <Stack spacing={1}>
        {items.map((item) => {
          // รายรับเทียบกับ deposit (ฝาก) ในรูป ส่วนรายจ่ายเทียบกับ withdraw (ถอน) — ต้อง
          // เลือกฟิลด์ให้ตรงกับ item.direction เดียวกับที่ backend ใช้ตอน match (ดู
          // matchOcrTransactions) ไม่งั้นยอด "ในรูป" ของรายรับจะโชว์ผิด/ว่างเปล่า
          const ocrAmount = item.direction === 'income'
            ? item.matchedTransaction?.deposit
            : item.matchedTransaction?.withdraw;
          return (
            <Paper
              key={item.paymentId}
              variant="outlined"
              sx={{ p: 1.25, borderRadius: 2, borderLeft: '4px solid', borderLeftColor: meta.accent }}
            >
              <Typography variant="body2" fontWeight={600}>
                {item.payment_number} — {new Date(item.date).toLocaleDateString('th-TH')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item.status === 'mismatched'
                  ? `ในระบบ: ${formatCurrency(item.totalAmount)} บาท / ในรูป: ${formatCurrency(ocrAmount)} บาท`
                  : `${formatCurrency(item.totalAmount)} บาท${
                    item.matchedTransaction?._split ? ` (รวม ${item.matchedTransaction._count} รายการ)` : ''
                  }`}
              </Typography>
              {(item.shadowIds || []).length > 0 && (
                <Typography variant="caption" sx={{ display: 'block', color: '#b45309', fontWeight: 600 }}>
                  จับคู่กับรายการเงาที่รอฝ่ายการเงินระบุ {item.shadowIds.length} รายการ — จะลบเงาเมื่อยืนยัน
                </Typography>
              )}
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}

function AccountInfoBox({ account }) {
  if (!account) return null;
  const hasAny = account.account_number || account.account_name
    || account.available_balance != null || account.account_balance != null
    || account.date_start || account.date_end;
  if (!hasAny) return null;
  return (
    <Paper variant="outlined" sx={{ mb: 2.5, p: 2, borderRadius: 2.5, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
      <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
        <WalletIcon fontSize="small" />
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        {(account.account_name || account.account_number) && (
          <Typography variant="body2" fontWeight={600}>
            {account.account_name || '-'} {account.account_number ? `(${account.account_number})` : ''}
          </Typography>
        )}
        {(account.available_balance != null || account.account_balance != null) && (
          <Typography variant="body2" color="text.secondary">
            ยอดเงินที่ใช้ได้: {formatCurrency(account.available_balance)} บาท
            {' / '}ยอดเงินในบัญชี: {formatCurrency(account.account_balance)} บาท
          </Typography>
        )}
        {(account.date_start || account.date_end) && (
          <Typography variant="caption" color="text.secondary">
            ช่วงวันที่: {account.date_start || '-'} ถึง {account.date_end || '-'}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

function TransactionRow({ t }) {
  const theme = useTheme();
  const isWithdraw = t.withdraw != null;
  const amount = isWithdraw ? t.withdraw : t.deposit;
  const accent = isWithdraw ? theme.palette.error.main : theme.palette.success.main;
  return (
    <Paper variant="outlined" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: 2 }}>
      <Avatar sx={{ bgcolor: alpha(accent, 0.12), color: accent, width: 36, height: 36 }}>
        {isWithdraw ? <WithdrawIcon fontSize="small" /> : <DepositIcon fontSize="small" />}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} sx={{ color: accent }}>
          {isWithdraw ? '-' : '+'}{formatCurrency(amount)} บาท
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {t.date}{t.time ? ` ${t.time}` : ''}{t.description ? ` — ${t.description}` : ''}
        </Typography>
      </Box>
    </Paper>
  );
}

export function OcrVerifyDialog({
  open, onClose, customerId, dateFrom, dateTo, companyOpeningBalance, companyOpeningBalanceDate,
  systemPayments, onConfirmed,
}) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [matching, setMatching] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [extracted, setExtracted] = useState(null); // { imageIds, imageUrls, transactions }
  const [matchResult, setMatchResult] = useState(null); // { results }
  // หลังยืนยันไปแล้ว 1 รอบ แต่ยังมี payment ในระบบที่ไม่เจอในรูปนี้ (not_found) — ไม่ปิด dialog
  // กลับไปหน้าแนบรูปเพื่อให้แนบรูปถัดไปยืนยันต่อได้ (วันที่รายการเยอะจนรูปเดียวไม่พอ)
  const [continueInfo, setContinueInfo] = useState(null); // { confirmed, remaining } | null
  // ผลตรวจยอดคงเหลือ (ocr-balance-check) ของผล match รอบนี้ — { systemNet, previous, amountDiffs }
  const [balanceCheck, setBalanceCheck] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState('');

  useEffect(() => {
    if (open) {
      setBalanceCheck(null);
      setBalanceLoading(false);
      setBalanceError('');
      setFiles([]);
      setDragOver(false);
      setExtracting(false);
      setMatching(false);
      setConfirming(false);
      setError('');
      setExtracted(null);
      setMatchResult(null);
      setContinueInfo(null);
    }
  }, [open]);

  // สร้าง object URL สำหรับพรีวิวรูปที่เลือกไว้ ต้อง revoke ทุกครั้งที่ files เปลี่ยน/dialog ปิด
  // กันหน่วยความจำรั่ว
  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [files]);

  const addFiles = (fileList) => {
    const imgs = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (imgs.length) setFiles((prev) => [...prev, ...imgs]);
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleExtract = async () => {
    if (files.length === 0) return;
    setExtracting(true);
    setError('');
    try {
      // ปรับภาพ (scale เข้าช่วงขนาดที่อ่านง่าย + เพิ่มความคมชัด) ก่อนส่งไป OCR — เหมือนที่ทำกับ
      // บิลเบิกน้ำมัน/odometer อยู่แล้ว ถ้าไฟล์ไหน preprocess ไม่ผ่าน (เช่นไม่ใช่รูปที่ canvas อ่านได้)
      // ใช้ไฟล์เดิมแทน ไม่ให้ทั้งชุดอัปโหลดล้มเพราะไฟล์เดียว
      const processedFiles = await Promise.all(
        files.map(async (file) => {
          try {
            const dataUrl = await fileToDataUrl(file);
            const base64 = await preprocessDocumentImage(dataUrl);
            return dataUrlBase64ToFile(base64, file.name || 'statement.jpg');
          } catch (preprocessError) {
            console.error('[OcrVerifyDialog] preprocess image failed, ใช้ไฟล์เดิม:', preprocessError);
            return file;
          }
        }),
      );
      const data = await dispatch(actions.extractCashFlowOcr({ files: processedFiles }));
      setExtracted(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setExtracting(false);
    }
  };

  const handleMatch = async () => {
    setMatching(true);
    setError('');
    try {
      const data = await dispatch(actions.matchCashFlowOcr({
        customerId,
        dateFrom,
        dateTo,
        ocrDateStart,
        ocrDateEnd,
        transactions: extracted.transactions,
      }));
      setMatchResult(data);
      loadBalanceCheck(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setMatching(false);
    }
  };

  // วันที่ใช้ตัดยอด = วันที่ที่กดยืนยัน (modal รายวันล็อก dateTo มาเสมอ) ไม่ใช้วันที่ที่ AI อ่าน
  const checkDate = dateTo || null;

  // ยอดในระบบ ณ สิ้นวัน checkDate (ทุกรายการที่เข้า/ออกแล้ว ไม่สนว่ายืนยันหรือยัง) — คิดล่วงหน้าเหมือน
  // กดยืนยันผลรอบนี้แล้ว (ดู accounting.service getOcrBalanceCheck) โหลดไม่ได้ไม่บล็อกการยืนยัน
  const loadBalanceCheck = async (match) => {
    if (!checkDate || !companyOpeningBalanceDate) return;
    setBalanceLoading(true);
    setBalanceError('');
    try {
      const data = await dispatch(actions.checkCashFlowOcrBalance({
        customerId,
        cutoff: companyOpeningBalanceDate,
        date: checkDate,
        results: match.results,
        unmatchedTransactions: match.unmatchedTransactions,
      }));
      setBalanceCheck(data);
    } catch (err) {
      setBalanceError(err.message);
    } finally {
      setBalanceLoading(false);
    }
  };

  const matchedItems = (matchResult?.results || []).filter((r) => r.status === 'matched');
  const mismatchedItems = (matchResult?.results || []).filter((r) => r.status === 'mismatched');
  const notFoundItems = (matchResult?.results || []).filter((r) => r.status === 'not_found');
  // รายการในรูปที่ยอด < payment ที่ยังไม่จับ (ทิศ+กรอบวันเดียวกัน) → น่าจะเป็นการโอนแยกที่แนบรูป
  // ไม่ครบ ยังไม่สร้าง shadow — ให้แนบรูปที่เหลือแล้วยืนยันซ้ำ (backend คัดแยกมาให้แล้ว)
  const pendingSplitItems = matchResult?.pendingSplitTransactions || [];

  const activeStep = matchResult ? 2 : (extracted ? 1 : 0);

  // ช่วงวันของรูป: ยึดจากวันของ "รายการที่อ่านได้" ไม่ยึด account.date_start/date_end ที่ AI
  // อ่าน header มักผิดปี (เจอ header = 2023 แต่ทุกรายการ = 2026) — ให้ตรงกับ backend
  const txnDates = (extracted?.transactions || []).map((t) => t.date).filter(Boolean).sort();
  const ocrDateStart = txnDates[0] || extracted?.account?.date_start;
  const ocrDateEnd = txnDates[txnDates.length - 1] || extracted?.account?.date_end;

  // รายการในระบบ (พรีวิวก่อน match) ที่อยู่ในช่วงวันของรูป ±DATE_MATCH_WINDOW_DAYS — ต้องเผื่อ
  // กรอบวันเท่ากับที่ matchOcrTransactions ฝั่ง backend ใช้ (วันโอนจริง != Payment.date) ไม่งั้น
  // เคส slip วันที่ 25 / Payment.date 26 จะกรอง payment ทิ้งหมด → พรีวิวว่างทั้งที่ backend จับคู่ได้
  const DATE_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;
  const systemPaymentsInOcrRange = extracted
    ? (systemPayments || []).filter((p) => {
      const t = new Date(`${new Date(p.date).toISOString().slice(0, 10)}T00:00:00`).getTime();
      const lo = ocrDateStart ? new Date(`${ocrDateStart}T00:00:00`).getTime() - DATE_WINDOW_MS : null;
      const hi = ocrDateEnd ? new Date(`${ocrDateEnd}T00:00:00`).getTime() + DATE_WINDOW_MS : null;
      return (lo == null || t >= lo) && (hi == null || t <= hi);
    })
    : [];

  // ตรวจยอดคงเหลือแบบตรงๆ: ยอดในระบบ ณ สิ้นวันที่ตรวจ (ยอดตั้งต้น + ทุกรายการที่เข้า/ออกแล้ว) เทียบ
  // ยอดในรูป — แทนสูตรเดิมที่นับเฉพาะรายการที่ยืนยันแล้ว (ไม่รวมรายการของวันที่กำลังยืนยันเอง ส่วนต่าง
  // จึงไม่เคยเป็น 0 และโตขึ้นทุกวันตามรายการที่ค้าง) — ตัดสินจาก "ส่วนต่างเพิ่มขึ้นจากจุดตรวจครั้งก่อน"
  // ถ้ามีจุดตรวจก่อนหน้า (ส่วนต่างเก่าที่ค้างมา เช่น ยอดตั้งต้นไม่ตรงธนาคาร ไม่ทำให้วันนี้ขึ้นไม่ตรง)
  // ยอดในรูป: "ยอดเงินที่ใช้ได้" ก่อน ถ้า AI อ่านได้ 0/ว่าง ใช้ "ยอดบัญชี" แทน (ตรงกับ backend _resolveBankBalance)
  const acc = extracted?.account;
  let ocrBankBalance = null;
  if (acc?.available_balance != null && Number(acc.available_balance) > 0) {
    ocrBankBalance = Number(acc.available_balance);
  } else if (acc?.account_balance != null) {
    ocrBankBalance = Number(acc.account_balance);
  } else if (acc?.available_balance != null) {
    ocrBankBalance = Number(acc.available_balance);
  }
  const openingBalance = Number(companyOpeningBalance || 0);
  const systemBalance = balanceCheck ? openingBalance + Number(balanceCheck.systemNet || 0) : null;
  const balanceDiff = systemBalance != null && ocrBankBalance != null ? ocrBankBalance - systemBalance : null;
  const previousCheck = balanceCheck?.previous || null;
  const previousDiff = previousCheck
    ? Number(previousCheck.bankBalance) - (openingBalance + Number(previousCheck.systemNet || 0))
    : null;
  const balanceChange = balanceDiff != null && previousDiff != null ? balanceDiff - previousDiff : balanceDiff;
  const balanceOk = balanceChange != null && Math.abs(balanceChange) < BALANCE_MATCH_TOLERANCE;
  const isBalanceMismatch = !!matchResult && balanceChange != null && !balanceOk;

  const handleConfirm = async () => {
    if (isBalanceMismatch) {
      const proceedAnyway = await confirmAsync(
        `ยอดคงเหลือในรูป (${formatCurrency(ocrBankBalance)} บาท) ไม่ตรงกับยอดในระบบ (${formatCurrency(systemBalance)} บาท) — ${previousCheck ? 'ส่วนต่างเพิ่มขึ้นจากครั้งก่อน' : 'ต่างกัน'} ${formatCurrency(Math.abs(balanceChange))} บาท กรุณาตรวจสอบก่อนยืนยัน ต้องการดำเนินการต่อหรือไม่?`,
      );
      if (!proceedAnyway) return;
    }
    // ไม่มีอะไรให้ยืนยันจริง (0 matched + 0 shadow) แต่มี pendingSplit → ข้ามการเรียก confirm
    // (backend จะ throw "ไม่มีรายการที่จะยืนยัน") ไปหน้าแนบรูปถัดไปเลย
    const nothingToConfirm = matchedItems.length === 0
      && (matchResult.unmatchedTransactions || []).length === 0;

    setConfirming(true);
    setError('');
    try {
      if (!nothingToConfirm) {
        await dispatch(actions.confirmCashFlowOcr({
          customerId,
          imageIds: extracted.imageIds,
          account: extracted.account,
          transactions: extracted.transactions,
          results: matchResult.results,
          unmatchedTransactions: matchResult.unmatchedTransactions,
          paymentIds: matchedItems.map((r) => r.paymentId),
          checkDate,
        }));
        if (onConfirmed) onConfirmed();
      }
      // ยังมี payment ในระบบที่ไม่เจอในรูปนี้ หรือมีรายการที่รอรูปเพิ่ม (การโอนแยกยังไม่ครบ) →
      // อาจอยู่ในรูปอื่น ยังไม่ปิด dialog กลับไปหน้าแนบรูปให้แนบรูปถัดไปต่อได้เลย (รอบใหม่ backend
      // จะ match กับ payment ที่ยังไม่ยืนยันเท่านั้น)
      if (notFoundItems.length > 0 || pendingSplitItems.length > 0) {
        setContinueInfo((prev) => ({
          confirmed: (prev?.confirmed || 0) + matchedItems.length,
          remaining: notFoundItems.length,
          pendingSplit: pendingSplitItems.length,
        }));
        setFiles([]);
        setExtracted(null);
        setMatchResult(null);
        setBalanceCheck(null);
        setBalanceError('');
        return;
      }
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setConfirming(false);
    }
  };

  const busy = extracting || matching || confirming;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1.5 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
            <VerifyIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>ยืนยัน OCR รายการรายจ่าย</Typography>
            <Typography variant="caption" color="text.secondary">อัปโหลดรูป → ตรวจสอบ → ยืนยัน</Typography>
          </Box>
        </Stack>
        <IconButton size="small" onClick={onClose} disabled={busy}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!extracted && (
          <Box>
            {continueInfo && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                ยืนยันแล้ว {continueInfo.confirmed} รายการ
                {continueInfo.remaining > 0 && ` · ยังเหลือ ${continueInfo.remaining} รายการที่ไม่พบในรูปนี้`}
                {continueInfo.pendingSplit > 0 && ` · ${continueInfo.pendingSplit} รายการรอรูปที่เหลือของการโอนแยก`}
                {' '}— แนบรูปถัดไปเพื่อยืนยันต่อ หรือกด &quot;ปิด&quot; ถ้าไม่มีรูปเพิ่ม
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              แนบรูปหน้าจอรายการเดินบัญชีจากแอปธนาคาร (ได้หลายรูป เผื่อรายการไม่พอในรูปเดียว)
              ระบบจะให้ AI อ่านรายการที่เจอในรูปให้ดูก่อน แล้วค่อยกดตรวจสอบกับรายการในระบบทีหลัง
            </Typography>
            <Box
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                addFiles(e.dataTransfer.files);
              }}
              sx={{
                border: '2px dashed',
                borderColor: dragOver ? 'primary.main' : 'divider',
                borderRadius: 2.5,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: dragOver ? alpha(theme.palette.primary.main, 0.06) : 'action.hover',
                transition: 'all 0.15s ease',
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
              />
              <UploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" fontWeight={600}>ลากรูปมาวาง หรือคลิกเพื่อเลือกรูป</Typography>
              <Typography variant="caption" color="text.secondary">รองรับ .jpg .png หลายไฟล์พร้อมกัน</Typography>
            </Box>

            {files.length > 0 && (
              <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mt: 2 }}>
                {files.map((file, idx) => (
                  <Paper
                    key={`${file.name}-${idx}`}
                    variant="outlined"
                    sx={{ position: 'relative', width: 88, borderRadius: 2, overflow: 'hidden' }}
                  >
                    <Box
                      component="img"
                      src={previews[idx]}
                      alt={file.name}
                      sx={{ width: '100%', height: 88, objectFit: 'cover', display: 'block' }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removeFile(idx)}
                      sx={{
                        position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(0,0,0,0.55)',
                        color: '#fff', width: 22, height: 22,
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block', px: 0.5, py: 0.25, textAlign: 'center',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}
                    >
                      {file.name}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        )}

        {extracting && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 1.5 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">กำลังให้ AI อ่านรายการจากรูป...</Typography>
          </Box>
        )}

        {extracted && !matchResult && !extracting && (
          <Box>
            <AccountInfoBox account={{ ...extracted.account, date_start: ocrDateStart, date_end: ocrDateEnd }} />
            <SystemPaymentsAccordion payments={systemPaymentsInOcrRange} totalCount={(systemPayments || []).length} />
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              รายการที่อ่านได้จากรูป ({extracted.transactions.length})
            </Typography>
            {extracted.transactions.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2, borderStyle: 'dashed' }}>
                <Typography variant="body2" color="text.secondary">
                  ไม่พบรายการในรูปเลย ลองเลือกรูปใหม่ (กด "ยกเลิก" แล้วเปิดใหม่)
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={1}>
                {extracted.transactions.map((t, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TransactionRow key={idx} t={t} />
                ))}
              </Stack>
            )}

            {matching && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 1.5 }}>
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary">กำลังตรวจสอบกับรายการในระบบ...</Typography>
              </Box>
            )}
          </Box>
        )}

        {matchResult && !matching && (
          <Box>
            <BalanceCheckBox
              loading={balanceLoading}
              error={balanceError}
              checkDate={checkDate}
              systemBalance={systemBalance}
              bankBalance={ocrBankBalance}
              diff={balanceDiff}
              previous={previousCheck}
              previousDiff={previousDiff}
              change={balanceChange}
              ok={balanceOk}
              amountDiffs={balanceCheck?.amountDiffs || []}
            />
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} sx={{ mb: 2.5 }}>
              <Chip
                size="small"
                icon={<CheckIcon sx={{ fontSize: '16px !important' }} />}
                color={STATUS_META.matched.color}
                variant={matchedItems.length ? 'filled' : 'outlined'}
                label={`ตรงกัน ${matchedItems.length}`}
              />
              <Chip
                size="small"
                icon={<WarningIcon sx={{ fontSize: '16px !important' }} />}
                color={STATUS_META.mismatched.color}
                variant={mismatchedItems.length ? 'filled' : 'outlined'}
                label={`ไม่ตรงกัน ${mismatchedItems.length}`}
              />
              <Chip
                size="small"
                icon={<NotFoundIcon sx={{ fontSize: '16px !important' }} />}
                variant={notFoundItems.length ? 'filled' : 'outlined'}
                label={`ไม่พบในรูป ${notFoundItems.length}`}
              />
              {pendingSplitItems.length > 0 && (
                <Chip
                  size="small"
                  color="info"
                  variant="outlined"
                  label={`รอรูปเพิ่ม ${pendingSplitItems.length}`}
                />
              )}
            </Stack>
            {pendingSplitItems.length > 0 && (
              <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
                มี {pendingSplitItems.length} รายการในรูป ที่ยอดน้อยกว่ายอดของ payment ที่ยังจับคู่ไม่ได้ —
                น่าจะเป็นการโอนแยกหลายครั้งแต่แนบรูปไม่ครบ ระบบจะยัง<b>ไม่สร้างรายการนอกระบบ</b> ให้แนบรูปที่เหลือแล้วกดยืนยันอีกครั้ง
              </Alert>
            )}
            <Divider sx={{ mb: 2.5 }} />
            <ResultGroup statusKey="matched" items={matchedItems} />
            <ResultGroup statusKey="mismatched" items={mismatchedItems} />
            <ResultGroup statusKey="not_found" items={notFoundItems} />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy}>{continueInfo ? 'ปิด' : 'ยกเลิก'}</Button>

        {!extracted && (
          <Button
            variant="contained"
            startIcon={extracting ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
            onClick={handleExtract}
            disabled={files.length === 0 || extracting}
          >
            {extracting ? 'กำลังอ่านรูป...' : 'อัปโหลดและอ่านรายการ'}
          </Button>
        )}

        {extracted && !matchResult && (
          <Button
            variant="contained"
            startIcon={matching ? <CircularProgress size={16} color="inherit" /> : <VerifyIcon />}
            onClick={handleMatch}
            disabled={extracted.transactions.length === 0 || matching}
          >
            {matching ? 'กำลังตรวจสอบ...' : 'ตรวจสอบกับรายการในระบบ'}
          </Button>
        )}

        {matchResult && (() => {
          const unmatchedCount = (matchResult.unmatchedTransactions || []).length;
          let label = `ยืนยันรายการที่ตรงกัน (${matchedItems.length})`;
          if (confirming) {
            label = 'กำลังยืนยัน...';
          } else if (matchedItems.length === 0 && unmatchedCount === 0 && pendingSplitItems.length > 0) {
            // มีแต่รายการรอรูปเพิ่ม — กดเพื่อไปหน้าแนบรูปถัดไป
            label = 'แนบรูปที่เหลือ';
          } else if (matchedItems.length === 0) {
            // ไม่มีรายการที่ match แต่มีรายการในรูปที่ไม่มีในระบบ — กดเพื่อบันทึกไว้รอฝ่ายการเงินระบุ
            label = `บันทึกรายการนอกระบบ (${unmatchedCount})`;
          }
          return (
            <Button
              variant="contained"
              color={isBalanceMismatch ? 'warning' : 'primary'}
              startIcon={confirming ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
              onClick={handleConfirm}
              disabled={(matchedItems.length === 0 && unmatchedCount === 0 && pendingSplitItems.length === 0) || confirming}
            >
              {label}
            </Button>
          );
        })()}
      </DialogActions>
    </Dialog>
  );
}

export default OcrVerifyDialog;
